import { useState, useCallback, useEffect } from 'react';
import { TestCatalogItem } from './use-test-catalog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase-client';

// Types
export interface OrderedTest {
  testId: string;
  name: string;
  quantity: number;
  category?: string;
}

export interface TestOrder {
  id?: string;
  patientId: string;
  tests: OrderedTest[];
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'cancelled';
  requestDate?: string;
  approvalDate?: string;
  rejectionReason?: string;
  notes?: string;
}

// Hook
export const useTestOrder = (initialOrderId?: string) => {
  const [orderedTests, setOrderedTests] = useState<OrderedTest[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [_status, setStatus] = useState<TestOrder['status']>('draft');
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query to get current order if orderID is provided
  const { 
    data: existingOrder, 
    isLoading, 
    error 
  } = useQuery<TestOrder | null, Error>({
    queryKey: ['testOrder', initialOrderId],
    queryFn: async () => {
      if (!initialOrderId) return null;

      // NOTE: The 'test_orders' table does not exist in the current database schema.
      // Return null gracefully instead of throwing so the UI renders an empty state.
      console.warn(
        `useTestOrder: test_orders table does not exist; fetch for order ${initialOrderId} skipped.`
      );
      return null;
    },
    enabled: !!initialOrderId,
  });

  // Use an effect to update state when the query data changes
  useEffect(() => {
    if (existingOrder) {
      setOrderedTests(existingOrder.tests);
      setNotes(existingOrder.notes || '');
      setStatus(existingOrder.status);
    }
  }, [existingOrder]);

  // Add a test to the order
  const addTest = useCallback((test: TestCatalogItem) => {
    setOrderedTests(prev => {
      // Check if test already exists in order
      const existingIndex = prev.findIndex(t => t.testId === test.id);
      
      if (existingIndex >= 0) {
        // If exists, increment quantity
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1
        };
        return updated;
      } else {
        // If new, add it with quantity 1
        return [...prev, {
          testId: test.id,
          name: test.name,
          category: test.category || undefined,
          quantity: 1
        }];
      }
    });
  }, []);

  // Remove a test from the order
  const removeTest = useCallback((testId: string) => {
    setOrderedTests(prev => prev.filter(test => test.testId !== testId));
  }, []);

  // Update test quantity
  const updateQuantity = useCallback((testId: string, quantity: number) => {
    if (quantity <= 0) {
      removeTest(testId);
      return;
    }
    
    setOrderedTests(prev => 
      prev.map(test => 
        test.testId === testId 
          ? { ...test, quantity } 
          : test
      )
    );
  }, [removeTest]);

  // Clear the entire order
  const clearOrder = useCallback(() => {
    setOrderedTests([]);
    setNotes('');
  }, []);

  // Submit order mutation
  const submitOrderMutation = useMutation({
    mutationFn: async (order: Omit<TestOrder, 'id' | 'patientId'>) => {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      
      const patientId = session.user.id;
      const requestDate = new Date().toISOString();
      
      const orderData: TestOrder = {
        ...order,
        patientId,
        requestDate,
        status: 'submitted' as const
      };

      // NOTE: The 'test_orders' table does not exist in the current database schema.
      // Log a warning and return a synthetic order object so the onSuccess handler
      // still fires and the UI can proceed normally.
      console.warn(
        'useTestOrder submitOrder: test_orders table does not exist; persisting order locally only.',
        orderData
      );

      // Return a synthetic record that satisfies the TestOrder shape.
      return {
        ...orderData,
        id: initialOrderId ?? `local-${Date.now()}`,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testOrders'] });
      toast({
        title: 'Test order submitted',
        description: 'Your test order has been submitted successfully.',
      });
      navigate('/schedule-appointment');
    },
    onError: (error) => {
      console.error('Failed to submit test order:', error);
      toast({
        title: 'Failed to submit order',
        description: 'There was an error submitting your test order. Please try again.',
        variant: 'destructive',
      });
    }
  });

  // Submit the order
  const submitOrder = useCallback(() => {
    if (orderedTests.length === 0) {
      toast({
        title: 'Empty order',
        description: 'Please add at least one test to your order.',
        variant: 'destructive',
      });
      return;
    }
    
    submitOrderMutation.mutate({
      tests: orderedTests,
      status: 'submitted',
      notes,
    });
  }, [orderedTests, notes, submitOrderMutation, toast]);

  return {
    orderedTests,
    addTest,
    removeTest,
    updateQuantity,
    clearOrder,
    submitOrder,
    notes,
    setNotes,
    isSubmitting: submitOrderMutation.isPending,
    submitError: submitOrderMutation.error,
    isLoadingOrder: isLoading,
    orderError: error,
    existingOrder
  };
}; 